import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText, Clock, CheckCircle2, CreditCard, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import StatusTracker from "@/components/StatusTracker";
import PortalLayout from "@/components/PortalLayout";
import MyDocuments from "@/pages/MyDocuments";
import MyPayments from "@/pages/MyPayments";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIdentity } from "@/context/SiteConfigContext";
import { formatUSD, STAGES, REQUIRES_CONFIRMATION } from "@/lib/constants";
import { useTranslation } from "react-i18next";

const STATUS_LABEL = {
  pending_payment: { text: "portal.statDue", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  in_progress: { text: "portal.statusInProgress", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  completed: { text: "portal.statusCompleted", cls: "bg-orange-100 text-orange-700 border-orange-200" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const identity = useIdentity();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data.requests || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const stats = {
    total: requests.length,
    active: requests.filter((r) => r.status === "in_progress").length,
    completed: requests.filter((r) => r.status === "completed").length,
    due: requests.filter((r) => r.payment_status !== "paid").length,
  };

  return (
    <PortalLayout active="overview">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="dashboard">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("client.welcomeName", { name: (user?.name || "").split(" ")[0] || t("client.founder") })}</h1>
              <p className="mt-1.5 text-muted-foreground">{t("client.dashSubtitle")}</p>
            </div>
            <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/company-formation")} data-testid="dashboard-new-request"><Plus className="h-4 w-4 mr-1.5" /> {t("client.newRequest")}</Button>
          </div>

          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={FileText} label={t("client.statTotal")} value={stats.total} />
            <StatCard icon={Clock} label={t("portal.statActive")} value={stats.active} accent="text-blue-600" />
            <StatCard icon={CheckCircle2} label={t("portal.statCompleted")} value={stats.completed} accent="text-orange-600" />
            <StatCard icon={CreditCard} label={t("portal.statDue")} value={stats.due} accent="text-amber-600" />
          </div>

          <div className="mt-10">
            <h2 className="font-heading text-xl tracking-tight mb-4 text-foreground">{t("client.yourRequests")}</h2>
            {loading ? (
              <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground card-soft">{t("client.loading")}</div>
            ) : requests.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center card-soft" data-testid="dashboard-empty">
                <FileText className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="mt-4 font-heading text-xl text-foreground">{t("client.noRequests")}</p>
                <p className="mt-1.5 text-sm text-muted-foreground">{t("client.noRequestsBody")}</p>
                <Button className="mt-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/company-formation")} data-testid="dashboard-empty-cta">{t("client.browseServices")} <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map((r, i) => {
                  const s = STATUS_LABEL[r.status] || STATUS_LABEL.pending_payment;
                  const saving = (r.line_items || []).filter((li) => li.amount < 0).reduce((a, li) => a + Math.abs(li.amount), 0);
                  return (
                    <motion.div key={r.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                      onClick={() => navigate(`/requests/${r.id}`)}
                      className="rounded-xl border border-border bg-card p-6 card-soft card-hover cursor-pointer" data-testid={`dashboard-request-${r.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-heading text-xl tracking-tight text-foreground">{r.package_name}</h3>
                            <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${s.cls}`}>{t(s.text)}</Badge>
                            {r.category === "Bundle" && <Badge className="rounded-sm border border-orange-200 bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px] uppercase tracking-wider">{t("client.bundle")}</Badge>}
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">{r.company_name}{r.jurisdiction ? ` · ${r.jurisdiction}` : ""}</p>
                          {saving > 0 && <p className="mt-1 text-xs font-medium text-green-700" data-testid={`dashboard-saving-${r.id}`}>{t("client.savedBundle", { amount: formatUSD(saving) })}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-heading text-lg text-foreground">{formatUSD(r.amount)}</p>
                          <p className="text-xs text-muted-foreground font-mono">{r.stage < STAGES.length ? t(`client.stage${r.stage}`) : t("client.stage4")}</p>
                        </div>
                      </div>
                      <div className="mt-6"><StatusTracker stage={r.stage} /></div>
                      {r.payment_status !== "paid" && (
                        <div className="mt-5 flex justify-end">
                          <Button size="sm" className="rounded-sm bg-amber-500 text-white hover:bg-amber-600" onClick={(e) => { e.stopPropagation(); navigate(`/requests/${r.id}`); }} data-testid={`dashboard-pay-${r.id}`}>{t("client.completePayment")}</Button>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          <MyDocuments />
          <MyPayments />
          <p className="mt-10 text-[11px] leading-relaxed text-muted-foreground/80" data-testid="dashboard-legal-line">
            {identity.legalName}{identity.regNumber !== REQUIRES_CONFIRMATION ? ` · ${t("client.regNo", { reg: identity.regNumber })}` : ""} · {identity.office} · {identity.email}
          </p>
      </div>
    </PortalLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent = "text-foreground" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 card-soft">
      <Icon className={`h-5 w-5 ${accent}`} />
      <p className="mt-4 font-heading text-3xl tracking-tight text-foreground">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    </div>
  );
}
